/**
 * Buyer-initiated returns / RMA service.
 *
 * State machine:
 *   requested -> approved | rejected
 *   approved  -> in_transit (via vendor recording shipment) -> received -> refunded | resolved_no_refund
 *
 * v1: refund is informational; the platform does NOT settle money.
 */

import { supabase } from '../client';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';

export type ShopReturnStatus =
  | 'requested'
  | 'approved'
  | 'rejected'
  | 'in_transit'
  | 'received'
  | 'refunded'
  | 'resolved_no_refund';

export type ShopReturnReasonCode =
  | 'damaged'
  | 'wrong_item'
  | 'quality_issue'
  | 'arrived_late'
  | 'no_longer_needed'
  | 'other';

export type ShopReturnRefundMethod = 'off_platform' | 'store_credit' | 'replacement';
export type ShopReturnPaidBy = 'vendor' | 'buyer' | 'platform';

export interface ShopReturn {
  id: string;
  return_number: number;
  order_id: string;
  shipment_id: string | null;
  vendor_id: string;
  buyer_user_id: string;
  status: ShopReturnStatus;
  reason: string | null;
  return_window_expires_at: string | null;
  vendor_decision_at: string | null;
  vendor_decision_by_user_id: string | null;
  vendor_decision_note: string | null;
  refund_cents: number | null;
  refund_currency: string | null;
  refund_method: ShopReturnRefundMethod | null;
  refund_recorded_at: string | null;
  refund_note: string | null;
  received_at: string | null;
  received_note: string | null;
  resolved_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  archived_by: string | null;
}

export interface ShopReturnLine {
  id: string;
  return_id: string;
  order_line_id: string;
  qty: number;
  reason_code: ShopReturnReasonCode;
  buyer_note: string | null;
  created_at: string;
}

export interface ShopReturnShipment {
  id: string;
  return_id: string;
  carrier_code: string | null;
  carrier_name: string | null;
  ship_mode: 'boat' | 'plane' | 'truck' | 'train' | null;
  tracking_number: string | null;
  tracking_url: string | null;
  shippo_tracker_id: string | null;
  label_url: string | null;
  paid_by: ShopReturnPaidBy;
  notes: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface ShopReturnEvent {
  id: string;
  return_id: string;
  event: string;
  actor_user_id: string | null;
  actor_kind: 'buyer' | 'vendor' | 'system' | 'admin' | null;
  payload: Record<string, unknown>;
  at: string;
}

// -------------------- eligibility --------------------

export interface ReturnEligibilityLine {
  orderLineId: string;
  productId: string;
  productName: string;
  qtyOrdered: number;
  qtyShipped: number;
  qtyAlreadyReturned: number;
  qtyEligible: number;
  windowDays: number;
  windowExpiresAt: string | null;
  eligible: boolean;
  notEligibleReason: string | null;
}

export interface ReturnEligibility {
  orderId: string;
  orderStatus: string;
  vendorId: string;
  vendorAcceptsReturns: boolean;
  lines: ReturnEligibilityLine[];
}

interface RawEligibilityLine {
  order_line_id: string;
  product_id: string;
  product_name: string;
  qty_ordered: number;
  qty_shipped: number;
  qty_already_returned: number;
  qty_eligible: number;
  window_days: number;
  window_expires_at: string | null;
  eligible: boolean;
  not_eligible_reason: string | null;
}

interface RawEligibility {
  order_id: string;
  order_status: string;
  vendor_id: string;
  vendor_accepts_returns: boolean;
  lines: RawEligibilityLine[];
}

/**
 * Normalizes the raw `jsonb` shape returned by
 * `shop.rpc_buyer_return_eligibility` into the camelCase contract that
 * UI components consume. Exported so the Vitest unit can verify the
 * wrapper's normalization without round-tripping through Postgres.
 */
export function normalizeReturnEligibility(raw: RawEligibility): ReturnEligibility {
  return {
    orderId: raw.order_id,
    orderStatus: raw.order_status,
    vendorId: raw.vendor_id,
    vendorAcceptsReturns: !!raw.vendor_accepts_returns,
    lines: (raw.lines ?? []).map((l) => ({
      orderLineId: l.order_line_id,
      productId: l.product_id,
      productName: l.product_name,
      qtyOrdered: Number(l.qty_ordered ?? 0),
      qtyShipped: Number(l.qty_shipped ?? 0),
      qtyAlreadyReturned: Number(l.qty_already_returned ?? 0),
      qtyEligible: Number(l.qty_eligible ?? 0),
      windowDays: Number(l.window_days ?? 0),
      windowExpiresAt: l.window_expires_at ?? null,
      eligible: !!l.eligible,
      notEligibleReason: l.not_eligible_reason ?? null,
    })),
  };
}

export async function fetchReturnEligibility(orderId: string): Promise<ReturnEligibility> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_return_eligibility', { p_order_id: orderId });
  if (error) throw error;
  return normalizeReturnEligibility(data as RawEligibility);
}

// -------------------- buyer + vendor mutations --------------------

export interface RequestReturnLineInput {
  orderLineId: string;
  qty: number;
  reasonCode: ShopReturnReasonCode;
  buyerNote?: string;
}

export async function requestReturn(input: {
  orderId: string;
  lines: RequestReturnLineInput[];
  buyerNote?: string;
  shipmentId?: string;
}): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_request_return', {
      p_order_id: input.orderId,
      p_lines: input.lines.map((l) => ({
        order_line_id: l.orderLineId,
        qty: l.qty,
        reason_code: l.reasonCode,
        buyer_note: l.buyerNote ?? null,
      })),
      p_buyer_note: input.buyerNote ?? null,
      p_shipment_id: input.shipmentId ?? null,
    });
  if (error) throw error;
  return data as string;
}

export async function vendorDecideReturn(input: {
  returnId: string;
  decision: 'approved' | 'rejected';
  note?: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_decide_return', {
      p_return_id: input.returnId,
      p_decision: input.decision,
      p_decision_note: input.note ?? null,
    });
  if (error) throw error;
}

export async function vendorRecordReturnShipment(input: {
  returnId: string;
  carrierCode: string;
  shipMode: 'boat' | 'plane' | 'truck' | 'train';
  trackingNumber: string;
  paidBy?: ShopReturnPaidBy;
  carrierName?: string;
  trackingUrl?: string;
  shippoTrackerId?: string;
  labelUrl?: string;
  notes?: string;
}): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_record_return_shipment', {
      p_return_id: input.returnId,
      p_carrier_code: input.carrierCode,
      p_ship_mode: input.shipMode,
      p_tracking_number: input.trackingNumber,
      p_paid_by: input.paidBy ?? 'vendor',
      p_label_url: input.labelUrl ?? null,
      p_carrier_name: input.carrierName ?? null,
      p_tracking_url: input.trackingUrl ?? null,
      p_shippo_tracker_id: input.shippoTrackerId ?? null,
      p_notes: input.notes ?? null,
    });
  if (error) throw error;
  return data as string;
}

export async function vendorMarkReturnReceived(input: {
  returnId: string;
  receivedNote?: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_mark_return_received', {
      p_return_id: input.returnId,
      p_received_note: input.receivedNote ?? null,
    });
  if (error) throw error;
}

export async function vendorRecordReturnRefund(input: {
  returnId: string;
  refundCents: number;
  refundMethod?: ShopReturnRefundMethod;
  refundNote?: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_record_refund', {
      p_return_id: input.returnId,
      p_refund_cents: input.refundCents,
      p_refund_method: input.refundMethod ?? 'off_platform',
      p_refund_note: input.refundNote ?? null,
    });
  if (error) throw error;
}

export async function vendorResolveReturnNoRefund(input: {
  returnId: string;
  resolutionNote?: string;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_resolve_no_refund', {
      p_return_id: input.returnId,
      p_resolution_note: input.resolutionNote ?? null,
    });
  if (error) throw error;
}

// -------------------- list / get --------------------

export interface ListReturnsInput extends KeysetInput {
  vendorId?: string;
  buyerUserId?: string;
  orderId?: string;
  status?: ShopReturnStatus;
}

export async function listReturns(input: ListReturnsInput): Promise<ListResult<ShopReturn>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('returns')
    .select('*', selectCountOption(input.countMode))
    .is('archived_at', null);

  if (input.vendorId) q = q.eq('vendor_id', input.vendorId);
  if (input.buyerUserId) q = q.eq('buyer_user_id', input.buyerUserId);
  if (input.orderId) q = q.eq('order_id', input.orderId);
  if (input.status) q = q.eq('status', input.status);

  q = applyKeyset(q, input.cursor, 'created_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopReturn[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getReturn(id: string): Promise<ShopReturn | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('returns')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopReturn | null;
}

export async function listReturnLines(returnId: string): Promise<ShopReturnLine[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('return_lines')
    .select('*')
    .eq('return_id', returnId)
    .order('created_at', { ascending: true })
    .order('id', { ascending: true })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ShopReturnLine[];
}

export async function listReturnShipments(returnId: string): Promise<ShopReturnShipment[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('return_shipments')
    .select('*')
    .eq('return_id', returnId)
    .is('archived_at', null)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as ShopReturnShipment[];
}

export async function listReturnEvents(returnId: string): Promise<ShopReturnEvent[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('return_event_log')
    .select('*')
    .eq('return_id', returnId)
    .order('at', { ascending: false })
    .order('id', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ShopReturnEvent[];
}
